import React, { useEffect, useRef, useState } from 'react';
import {
	Alert,
	Button,
	Grid2,
	LinearProgress,
	List,
	ListItem,
	Snackbar,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	LandingImage,
	LPStep01,
	LPStep02,
	LPStep03,
	LPStep04,
	LPStep05,
} from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import SideNavigationBar from '../components/SideNavigationBar';
import { useMainCustomStyle } from '../assets/theme/Theme';
import {
	getAccessToken,
	getUserEmail,
	getUserId,
	getUserType,
	setAllFeatureLimitStatus,
	setFeatureLimit,
	setFeatureLimitStatus,
	setUserType,
} from '../Utils/AuthUtil';
import { fetchGuestUserLimits, onAddClickStat } from '../services/UserService';
import { MAX_LIMITS, USER_TYPES } from '../Utils/Enums';
import { useNavigate } from 'react-router-dom';
import LimitAlert from '../components/LimitAlert';
import LimitationPopup from '../components/LimitationPopup';

interface StepTileProps {
	step: string;
	type: string;
	TileIcon: React.ElementType;
	description: string;
	onPressTile: () => void;
}

interface LimitationTileProps {
	count: string;
	countPercentage: number;
	label: string;
}

export function StepTile({
	step,
	type,
	TileIcon,
	description,
	onPressTile,
}: StepTileProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	return (
		<Grid2
			component={'div'}
			size={{ xs: 12, md: 4 }}
			sx={{
				backgroundColor: '#FCFDFF',
				borderRadius: '6px',
				flexDirection: 'row',
				alignItems: 'center',
				display: 'flex',
				paddingX: '16px',
				paddingY: '12px',
				border: '0.6px solid #F1F5F999',
				cursor: 'pointer',
			}}
			onClick={onPressTile}>
			<Grid2
				size={{ xs: 2 }}
				display="flex"
				justifyContent="center"
				alignItems="center">
				<TileIcon />
			</Grid2>
			<Grid2
				size={{ xs: 10 }}
				sx={{
					flexDirection: 'column',
					marginLeft: isMobile ? '10px' : '5px',
				}}>
				<Typography
					color={'#64748B'}
					fontSize={'11px'}
					paddingBottom={'5px'}
					fontWeight={'400'}>
					{step}
				</Typography>
				<Typography
					color={'#00000'}
					fontSize={'14px'}
					paddingY={'1.5px'}
					fontWeight={'600'}>
					{type}
				</Typography>
				<Typography
					color={'#6B7280'}
					fontSize={'12px'}
					paddingTop={'5px'}
					fontWeight={'500'}>
					{description}
				</Typography>
			</Grid2>
		</Grid2>
	);
}

function LimitationTile({
	count,
	countPercentage,
	label,
}: LimitationTileProps) {
	function getProgressColor(value: number) {
		if (value < 50) {
			return MAIN_THEME_COLOR;
		} else if (value >= 50 && value < 70) {
			return '#DDA01E';
		} else {
			return '#FF4343';
		}
	}

	return (
		<Grid2 component={'div'} size={{ xs: 12, md: 6 }}>
			<Grid2 container width={'100%'} flexDirection={'column'}>
				<Grid2
					container
					width={'100%'}
					flexDirection={'row'}
					justifyContent={'space-between'}
					paddingBottom={'10px'}>
					<Typography
						fontSize={'14px'}
						color={'#324054'}
						fontWeight={'400'}>
						{label}
					</Typography>
					<Typography
						fontSize={'14px'}
						color={'#324054'}
						fontWeight={'400'}>
						{count}
					</Typography>
				</Grid2>
				<LinearProgress
					variant="determinate"
					value={countPercentage}
					sx={{
						height: 5,
						borderRadius: '10px',
						backgroundColor: '#F5F5F5',
						'& .MuiLinearProgress-bar': {
							backgroundColor: getProgressColor(countPercentage),
						},
					}}
				/>
			</Grid2>
		</Grid2>
	);
}

function GuestLandingPage() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const navigator = useNavigate();
	const bottomRef = useRef<HTMLDivElement | null>(null);
	const mainDivStyle = useMainCustomStyle();
	const [isLimitReached, setIsLimitReached] = useState(false);
	const [limits, setLimits] = useState({
		fileUpload: 0,
		websiteUpload: 0,
		websiteUrl: 0,
		testChats: 0,
	});
	const [open, setOpen] = useState(false);
	const [isAlertPopupOpen, setIsAlertPopupOpen] = useState(false);
	const [isUserHasFullAccess, setIsUserHasFullAccess] = useState(false);

	useEffect(() => {
		if (isUserLoggedIn()) {
			getUserLimits();
		}
	}, []);

	function isUserLoggedIn() {
		const token = getAccessToken();
		const userId = getUserId();

		return !!(token && userId);
	}

	async function getUserLimits() {
		const token = getAccessToken();
		const userId = getUserId();

		if (userId && token) {
			const data = await fetchGuestUserLimits(userId, token);
			if (data.userType === USER_TYPES[0].key) {
				setUserType(USER_TYPES[0].key);
				setFeatureLimitStatus(data.isAllLimitReached);
				setAllFeatureLimitStatus({});
				setFeatureLimit({});
				setIsUserHasFullAccess(true);
				setOpen(false);
			} else {
				setUserType(USER_TYPES[1].key);
				setIsLimitReached(data.isAllLimitReached);
				setLimits(data.remainingLimit);
				setFeatureLimit(data.remainingLimit);
				setAllFeatureLimitStatus(data.isAllLimitReached);
				setFeatureLimitStatus(data.reachedLimits);
				setOpen(data.isAllLimitReached ? true : false);
			}
		}
	}

	function handleClose() {
		setOpen(false);
	}

	function onOpenDetailsPopup() {
		setOpen(false);
		setIsAlertPopupOpen(true);
	}

	const scrollToBottom = () => {
		if (isUserLoggedIn()) {
			if (bottomRef.current) {
				bottomRef.current.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				});
			}
		} else {
			navigator('/login');
		}
	};

	function isFullAccessUser() {
		return getUserType() === USER_TYPES[0].key && isUserHasFullAccess;
	}

	function getLimitText(limit: number, maxLimit: number) {
		if (isFullAccessUser()) {
			return 'Unlimited';
		} else {
			return `${limit} / ${maxLimit}`;
		}
	}

	function getLimitProgress(limit: number, maxLimit: number) {
		if (isFullAccessUser()) {
			return 0;
		} else {
			return Math.floor((limit / maxLimit) * 100);
		}
	}

	function onPressInfo() {
		onAddClickStat(
			isUserLoggedIn() ? (getUserId() ?? '') : '',
			'Upgrade info widget',
			isUserLoggedIn(),
			'Home',
			getUserEmail() ? getUserEmail() : 'N/A',
			!isUserLoggedIn() ? getVisitorId() : 'N/A',
		);

		if (isUserLoggedIn()) {
			window.open('https://www.senzmate.com/company/contact/', '_blank');
		}
	}

	function onPressNavigation(route: string, clickedItem: string) {
		onAddClickStat(
			isUserLoggedIn() ? (getUserId() ?? '') : '',
			clickedItem,
			isUserLoggedIn(),
			'Home',
			getUserEmail() ? getUserEmail() : 'N/A',
			!isUserLoggedIn() ? getVisitorId() : 'N/A',
		);

		navigator(isUserLoggedIn() ? route : '/login');
	}

	function getVisitorId() {
		let visitorId = localStorage.getItem('visitor_id');
		if (!visitorId) {
			visitorId = crypto.randomUUID(); // Or use a simple timestamp + random string
			localStorage.setItem('visitor_id', visitorId);
		}
		return visitorId;
	}

	return (
		<>
			{isUserLoggedIn() && <SideNavigationBar />}
			<div
				style={
					isUserLoggedIn()
						? mainDivStyle
						: {
								backgroundColor: '#f1f9f8',
								height: isMobile ? '100%' : '100vh',
								justifyContent: 'center',
								display: 'flex',
							}
				}>
				<Grid2
					container
					paddingLeft={'20px'}
					paddingRight={'20px'}
					paddingTop={isUserLoggedIn() ? '0px' : '10px'}>
					<Grid2
						container
						sx={{
							width: '100%',
							alignItems: 'center',
							justifyContent: 'center',
						}}>
						<Grid2
							component={'div'}
							size={{ xs: 12, md: 6 }}
							sx={{ paddingX: '16px', marginBottom: '20px' }}>
							<Typography
								sx={{
									borderRadius: 100,
									color: '#047857',
									fontSize: '10px',
									backgroundColor: '#FFFFFF',
									width: 150,
									padding: '5px',
									textAlign: 'center',
								}}>
								New • Sandbox Experience
							</Typography>
							<Typography
								fontSize={'34px'}
								color={'#000000'}
								fontWeight={'700'}>
								Experience Trip Planner.Ai Sandbox
							</Typography>
							<Typography
								fontSize={'14px'}
								color={'#64748B'}
								fontWeight={'400'}>
								Integrate our AI assistant seamlessly into your
								business WhatsApp or website. Experience full
								transparency, with both manual and automated
								handover options available through the Trip
								Planner.Ai Portal, ensuring a personalized
								assistant experience.
							</Typography>
							<Typography
								fontSize={'14px'}
								color={'#000000'}
								marginTop={'5px'}
								fontWeight={'600'}>
								In the Sandbox, you can try this experience with
								limited access.
							</Typography>
							<List sx={{ listStyle: 'disc', pl: 4 }}>
								<ListItem
									sx={{
										display: 'list-item',
										color: '#7C7C7C',
										paddingY: '0px',
										paddingLeft: '0px',
									}}>
									<Typography
										fontSize={'12px'}
										color={'#7C7C7C'}>
										Studio Chat → Simulated as a real
										integrated end-user bot.
									</Typography>
								</ListItem>
								<ListItem
									sx={{
										display: 'list-item',
										color: '#7C7C7C',
										paddingBottom: '0px',
										paddingLeft: '0px',
									}}>
									<Typography
										fontSize={'12px'}
										color={'#7C7C7C'}>
										Conversation View → Consultant-level
										transparency of chats between bot and
										end-user.
									</Typography>
								</ListItem>
								<ListItem
									sx={{
										display: 'list-item',
										color: '#7C7C7C',
										paddingBottom: '0px',
										paddingLeft: '0px',
									}}>
									<Typography
										fontSize={'12px'}
										color={'#7C7C7C'}>
										Human Intervention → Explore takeover
										capabilities.
									</Typography>
								</ListItem>
							</List>
							<Typography
								fontSize={'12px'}
								color={'#000000'}
								marginTop={'5px'}
								fontWeight={'500'}>
								Reach out to us for full product access and
								integration support
							</Typography>
							<Button
								onClick={() =>
									onPressNavigation(
										'/knowledgeUpload',
										'Explore Button',
									)
								}
								style={{
									boxShadow: 'none',
									marginTop: '10px',
									textTransform: 'none',
									backgroundColor: MAIN_THEME_COLOR,
								}}
								variant="contained">
								Start Exploring
							</Button>
						</Grid2>
						<Grid2
							component={'div'}
							size={{ xs: 12, md: 6 }}
							sx={{
								display: { xs: 'none', md: 'flex' },
								justifyContent: 'center',
							}}>
							<LandingImage />
						</Grid2>
					</Grid2>
					<Grid2 container width={'100%'} flexDirection={'column'}>
						<Grid2 sx={{ paddingX: '16px' }}>
							<Typography
								fontSize={'18px'}
								color={'#000000'}
								fontWeight={'700'}>
								How it&apos;s works
							</Typography>
							<Typography
								fontSize={'14px'}
								color={'#64748B'}
								fontWeight={'400'}>
								A quick 5-step flow to explore Trip Planner.Ai
							</Typography>
						</Grid2>
						<Grid2
							container
							spacing={1}
							size={{ xs: 12, md: 12, sm: 6 }}
							sx={{
								flexGrow: 1,
								overflowY: 'auto',
								marginY: '15px',
								paddingX: '16px',
							}}>
							<StepTile
								step={'Step 01'}
								type={'Upload Knowledge Base'}
								TileIcon={LPStep01}
								description={
									'Bot will answer based on the uploaded knowledge.'
								}
								onPressTile={() =>
									onPressNavigation(
										'/knowledgeUpload',
										'Knowledge upload widget',
									)
								}
							/>
							<StepTile
								step={'Step 02'}
								type={'Test Chat with AI Assistant'}
								TileIcon={LPStep02}
								description={
									'Experience how end-users interact with the bot.'
								}
								onPressTile={() =>
									onPressNavigation(
										'/knowledgeUpload',
										'Test chat widget',
									)
								}
							/>
							<StepTile
								step={'Step 03'}
								type={'Try Consultant Connection in Open View'}
								TileIcon={LPStep03}
								description={
									'View chat history, transparency between bot and user, and test consultant takeover for active sessions.'
								}
								onPressTile={() =>
									onPressNavigation(
										'/chats',
										'Consultant chat widget',
									)
								}
							/>
							<StepTile
								step={'Step 04'}
								type={'Monitor Your Sandbox Usage'}
								TileIcon={LPStep04}
								description={
									'Track your session limits and uploaded content.'
								}
								onPressTile={() => {
									scrollToBottom();
									onPressNavigation(
										'',
										'Sandbox usage widget',
									);
								}}
							/>
							{!isFullAccessUser() && (
								<StepTile
									step={'Step 05'}
									type={'Upgrade Anytime'}
									TileIcon={LPStep05}
									description={
										'Unlock full access and get integration support for WhatsApp & Website.'
									}
									onPressTile={onPressInfo}
								/>
							)}
						</Grid2>
					</Grid2>
					{isUserLoggedIn() && !isFullAccessUser() && (
						<Grid2
							container
							width={'100%'}
							flexDirection={'column'}
							marginTop={'10px'}
							sx={{ paddingX: '16px' }}>
							<Grid2>
								<Typography
									fontSize={'18px'}
									color={'#000000'}
									fontWeight={'700'}>
									Your Sandbox Usage
								</Typography>
								<Typography
									fontSize={'14px'}
									color={'#64748B'}
									fontWeight={'400'}>
									Color-coded based on your limits
								</Typography>
							</Grid2>
							<Grid2
								container
								sx={{
									border: '1px solid #E2E8F0',
									width: '100%',
									flexDirection: 'column',
									padding: '16px',
									marginTop: '10px',
									borderRadius: '8px',
									backgroundColor: '#FFFFFF',
								}}>
								<Grid2
									container
									columnGap={5}
									rowGap={2}
									sx={{
										flexDirection: 'row',
										marginTop: '10px',
										padding: '10px',
										flexWrap: {
											xs: 'wrap',
											sm: 'nowrap',
										},
									}}>
									<LimitationTile
										count={getLimitText(
											limits.fileUpload,
											MAX_LIMITS.fileUpload,
										)}
										countPercentage={getLimitProgress(
											limits.fileUpload,
											MAX_LIMITS.fileUpload,
										)}
										label={'Files'}
									/>
									<LimitationTile
										count={getLimitText(
											limits.websiteUpload,
											MAX_LIMITS.websiteUpload,
										)}
										countPercentage={getLimitProgress(
											limits.websiteUpload,
											MAX_LIMITS.websiteUpload,
										)}
										label={'Websites'}
									/>
								</Grid2>
								<Grid2
									container
									columnGap={5}
									rowGap={2}
									sx={{
										flexDirection: 'row',
										padding: '10px',
										flexWrap: {
											xs: 'wrap',
											sm: 'nowrap',
										},
									}}>
									<LimitationTile
										count={getLimitText(
											limits.websiteUrl,
											MAX_LIMITS.websiteUrl,
										)}
										countPercentage={getLimitProgress(
											limits.websiteUrl,
											MAX_LIMITS.websiteUrl,
										)}
										label={'Pages'}
									/>
									<LimitationTile
										count={getLimitText(
											limits.testChats,
											MAX_LIMITS.testChats,
										)}
										countPercentage={getLimitProgress(
											limits.testChats,
											MAX_LIMITS.testChats,
										)}
										label={'Test Chat Sessions'}
									/>
								</Grid2>
							</Grid2>
						</Grid2>
					)}
					<div ref={bottomRef} />
				</Grid2>
				<LimitAlert
					isAlertOpen={open}
					handleClose={handleClose}
					handleActionButtonClick={onOpenDetailsPopup}
				/>
				<LimitationPopup
					open={isAlertPopupOpen}
					onClose={() => {
						setIsAlertPopupOpen(false);
						setOpen(true);
					}}
				/>
			</div>
		</>
	);
}

export default GuestLandingPage;
